import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareIcon from '@mui/icons-material/Share';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { ReferralSummary } from '../types';
import { fetchDriverReferrals } from '../services/driverWorkspaceService';

const Referrals: React.FC = () => {
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchDriverReferrals()
      .then(setSummary)
      .finally(() => setLoading(false));
  }, []);

  const copy = () => {
    if (!summary?.shareUrl) return;
    navigator.clipboard.writeText(summary.shareUrl).catch(() => {});
    setMessage('Referral link copied.');
  };

  const shareWhatsApp = () => {
    if (!summary?.shareUrl) return;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`Join RideSaathi and get taxi rides free: ${summary.shareUrl}`)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const shareNative = async () => {
    if (!summary?.shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join RideSaathi',
          text: 'Get organized taxi rides. Join using my link:',
          url: summary.shareUrl,
        });
      } catch {
        copy();
      }
    } else {
      copy();
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  if (!summary) {
    return <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>Referral details are not available.</Paper>;
  }

  const milestones = summary.milestones || [];
  const nextMilestone = milestones.find((m) => !m.completed);
  const progressToNext = nextMilestone
    ? Math.min((summary.referralCount / nextMilestone.target) * 100, 100)
    : 100;

  return (
    <Stack spacing={3} sx={{ maxWidth: 960, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          bgcolor: '#061b31',
          color: 'white',
          background: 'linear-gradient(135deg,#061b31,#0b3156)',
        }}
      >
        <Typography variant="h4" fontWeight={900}>Refer drivers. Earn credits.</Typography>
        <Typography color="rgba(255,255,255,0.72)" mt={0.75}>
          Share your invite link with taxi partners. Bonus credits are added to your wallet for every driver who joins.
        </Typography>
      </Paper>

      {/* Stats row */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={4}>
          <Paper variant="outlined" sx={{ p: 2.25, height: '100%' }}>
            <Stack spacing={0.5}>
              <Box sx={{ color: '#b98500' }}><GroupAddIcon /></Box>
              <Typography variant="caption" color="text.secondary">Drivers joined</Typography>
              <Typography variant="h4" fontWeight={900}>{summary.referralCount}</Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={6} md={4}>
          <Paper variant="outlined" sx={{ p: 2.25, height: '100%' }}>
            <Stack spacing={0.5}>
              <Box sx={{ color: '#b98500' }}><EmojiEventsIcon /></Box>
              <Typography variant="caption" color="text.secondary">Bonus unlocks earned</Typography>
              <Typography variant="h4" fontWeight={900}>{summary.bonusUnlocks}</Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.25, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Your referral code</Typography>
            <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: 2 }}>{summary.referralCode}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Milestone progress */}
      {milestones.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="h6" fontWeight={900}>Milestone progress</Typography>
            {nextMilestone && (
              <Typography variant="caption" color="text.secondary">
                {summary.referralCount} / {nextMilestone.target} to next reward
              </Typography>
            )}
          </Box>
          {nextMilestone && (
            <LinearProgress
              variant="determinate"
              value={progressToNext}
              sx={{ height: 10, borderRadius: 5, mb: 2, bgcolor: 'rgba(7,24,45,0.08)' }}
              color="success"
            />
          )}
          <Stack spacing={1.25}>
            {milestones.map((milestone) => (
              <Box
                key={milestone.target}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                sx={{ opacity: milestone.completed ? 1 : 0.65 }}
              >
                <Box>
                  <Typography fontWeight={800}>
                    {milestone.target} referral{milestone.target > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    +{milestone.rewardCredits} bonus credit{milestone.rewardCredits > 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Chip
                  label={milestone.completed ? 'Completed' : 'Pending'}
                  color={milestone.completed ? 'success' : 'default'}
                  variant={milestone.completed ? 'filled' : 'outlined'}
                  size="small"
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Shareable card */}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={900} mb={2}>Share your link</Typography>
        <TextField
          fullWidth
          value={summary.shareUrl}
          sx={{ mb: 2 }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={copy}><ContentCopyIcon /></IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="contained" color="secondary" startIcon={<ContentCopyIcon />} onClick={copy}>
            Copy link
          </Button>
          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            onClick={shareWhatsApp}
            sx={{ borderColor: '#25d366', color: '#25d366', '&:hover': { borderColor: '#1da34e', bgcolor: 'rgba(37,211,102,0.06)' } }}
          >
            Share on WhatsApp
          </Button>
          <Button variant="outlined" startIcon={<ShareIcon />} onClick={shareNative}>
            Share
          </Button>
        </Stack>
      </Paper>

      {/* How it works */}
      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#f8faff' }}>
        <Typography variant="h6" fontWeight={900} mb={1.5}>How it works</Typography>
        <Grid container spacing={2}>
          {[
            { step: '1', title: 'Share your link', body: 'Send your unique referral link to driver friends via WhatsApp or any app.' },
            { step: '2', title: 'They sign up', body: 'When a driver registers using your link, they are counted as your referral.' },
            { step: '3', title: 'You earn credits', body: 'Bonus unlock credits are added to your wallet at each milestone.' },
          ].map((item) => (
            <Grid key={item.step} item xs={12} sm={4}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#07182d',
                    color: '#ffc928',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 900,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {item.step}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.body}</Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar open={Boolean(message)} autoHideDuration={2500} onClose={() => setMessage(null)} message={message} />
    </Stack>
  );
};

export default Referrals;
